<template>
  <div>
    <div v-if="showFilters" class="gp-adm-filters" :style="filterGridStyle">
      <div v-for="header in filterHeaders" :key="header.value" class="gp-adm-filter">
        <label>{{ header.text }}</label>
        <select v-if="filterChoices(header).length" v-model="filters[header.value]" class="form-select form-control">
          <option value="">Todos</option>
          <option v-for="choice in filterChoices(header)" :key="String(choice.value)" :value="choice.value">{{ choice.title }}</option>
        </select>
        <input v-else v-model.trim="filters[header.value]" type="text" class="form-control" :placeholder="header.text" />
      </div>
      <button type="button" class="btn btn-outline-secondary gp-adm-clear" @click="clearFilters"><i class="fas fa-eraser"></i> Limpar</button>
    </div>
    <div class="gp-adm-toolbar">
      <span>{{ filteredItems.length }} registro(s) encontrado(s)</span>
      <div class="gp-adm-toolbar-actions">
        <div v-if="totalField" class="gp-adm-total"><span>{{ totalLabel }}</span><strong>R$ {{ formatCurrency(filteredTotal) }}</strong></div>
        <button v-if="showExport" type="button" class="btn gp-export-button" :disabled="!filteredItems.length" @click="exportExcel"><v-icon size="16">mdi-microsoft-excel</v-icon> Exportar Excel</button>
      </div>
    </div>
    <garante-pay-data-table v-bind="$attrs" :headers="visibleHeaders" :items="filteredItems" :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
      <template v-for="(_, slotName) in $slots" #[slotName]="slotProps"><slot :name="slotName" v-bind="slotProps || {}" /></template>
    </garante-pay-data-table>
  </div>
</template>

<script>
export default {
  name: 'AdmDataTable', inheritAttrs: false,
  props: {
    headers: { type: Array, required: true }, items: { type: Array, required: true },
    modelValue: { type: Array, default: () => [] }, fileName: { type: String, default: 'registros' },
    filterOptions: { type: Object, default: () => ({}) },
    showFilters: { type: Boolean, default: true },
    showExport: { type: Boolean, default: true },
    filterColumns: { type: Number, default: null },
    exportMetadata: { type: Array, default: () => [] },
    exportFormatters: { type: Object, default: () => ({}) },
    exportHeaders: { type: Array, default: () => [] },
    filterSourceHeaders: { type: Array, default: () => [] },
    exportNumberFormats: { type: Object, default: () => ({}) },
    totalField: { type: String, default: '' },
    totalLabel: { type: String, default: 'Valor total filtrado' }
  },
  emits: ['update:modelValue'],
  data: () => ({ filters: {} }),
  computed: {
    filterGridStyle () {
      const columns = this.filterColumns || Math.min(Math.max(this.filterHeaders.length + 1, 1), 4)
      return { '--gp-adm-filter-columns': columns }
    },
    visibleHeaders () {
      return this.headers.filter(header => !header.hideColumn)
    },
    filterHeaders () {
      const source = this.filterSourceHeaders.length ? this.filterSourceHeaders : this.headers
      return source.filter(header => header.value !== 'actions' && !header.hideFilter && this.normalize(header.text) !== 'ordem')
    },
    filteredItems () {
      return this.items.filter(item => this.filterHeaders.every(header => {
        const filter = this.filters[header.value]
        if (filter === '' || filter === null || filter === undefined) return true
        const raw = this.getHeaderValue(item, header)
        if (this.filterChoices(header).length) return String(raw) === String(filter)
        if (typeof filter === 'boolean') return raw === filter
        return this.normalize(raw).includes(this.normalize(filter))
      }))
    },
    filteredTotal () {
      if (!this.totalField) return 0
      return this.filteredItems.reduce((total, item) => total + this.toNumber(this.getValue(item, this.totalField)), 0)
    }
  },
  methods: {
    getValue (item, path) { return String(path).split('.').reduce((value, key) => value?.[key], item) },
    getHeaderValue (item, header) { return typeof header.filterAccessor === 'function' ? header.filterAccessor(item) : this.getValue(item, header.value) },
    normalize (value) { return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() },
    toNumber (value) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0
      const text = String(value ?? '').trim().replace(/R\$/gi, '').replace(/\s/g, '')
      if (!text) return 0
      const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text
      const number = Number(normalized)
      return Number.isFinite(number) ? number : 0
    },
    formatCurrency (value) { return Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    filterChoices (header) {
      if (this.filterOptions[header.value]) return this.filterOptions[header.value]
      const values = this.items.map(item => this.getHeaderValue(item, header)).filter(value => value !== null && value !== undefined)
      return values.length && values.every(value => typeof value === 'boolean') ? [{ title: 'Sim', value: true }, { title: 'Não', value: false }] : []
    },
    clearFilters () { this.filters = {} },
    async exportExcel () {
      const XLSX = await import('xlsx')
      const exportHeaders = (this.exportHeaders.length ? this.exportHeaders : this.headers)
        .filter(header => header.value !== 'select' && header.value !== 'actions' && !header.hideExport)
      const data = this.filteredItems.map(item => exportHeaders.map(header => {
        const raw = this.getHeaderValue(item, header)
        const choice = this.filterChoices(header).find(option => option.value == raw)
        const formatter = this.exportFormatters[header.value]
        return formatter ? formatter(raw, item) : (choice?.title ?? raw ?? '')
      }))
      const rows = [
        ...this.exportMetadata.map(info => [info.label, info.value ?? '']),
        ...(this.exportMetadata.length ? [[]] : []),
        exportHeaders.map(header => header.text),
        ...data
      ]
      const sheet = XLSX.utils.aoa_to_sheet(rows), book = XLSX.utils.book_new()
      const firstDataRow = this.exportMetadata.length + (this.exportMetadata.length ? 1 : 0) + 1
      exportHeaders.forEach((header, columnIndex) => {
        const numberFormat = this.exportNumberFormats[header.value]
        if (!numberFormat) return
        data.forEach((row, rowIndex) => {
          const cell = sheet[XLSX.utils.encode_cell({ r: firstDataRow + rowIndex, c: columnIndex })]
          if (cell && cell.t === 'n') cell.z = numberFormat
        })
      })
      sheet['!cols'] = exportHeaders.map((header, index) => ({
        wch: Math.min(45, Math.max(header.text.length + 2, ...data.map(row => String(row[index] ?? '').length + 2)))
      }))
      XLSX.utils.book_append_sheet(book, sheet, 'Registros')
      XLSX.writeFile(book, `${this.fileName}-filtrados-${this.$moment().format('YYYY-MM-DD-HHmm')}.xlsx`)
    }
  }
}
</script>

<style scoped>
.gp-adm-filters{display:grid;grid-template-columns:repeat(var(--gp-adm-filter-columns,auto-fit),minmax(150px,1fr));align-items:end;gap:12px;margin-bottom:10px;padding:14px;border:1px solid #e1d9cd;border-left:4px solid #e77900;border-radius:12px;background:#fcf8f2}.gp-adm-filter label{display:block;margin-bottom:5px;color:#4d586d;font-size:11px;font-weight:750}.gp-adm-filter .form-control,.gp-adm-clear{min-height:38px;border-color:#d8d2c8;border-radius:9px;font-size:12px}.gp-adm-toolbar{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:0 2px 10px;color:#68748a;font-size:11.5px;font-weight:650}.gp-adm-toolbar-actions{display:flex;align-items:center;justify-content:flex-end;gap:10px}.gp-adm-total{display:flex;align-items:center;gap:7px;padding:7px 11px;border:1px solid #e1d9cd;border-radius:9px;background:#fcf8f2;font-size:10.5px}.gp-adm-total strong{color:#102a5a;font-size:12px;font-weight:800}.gp-export-button{display:inline-flex;align-items:center;gap:6px;border:1px solid #a9d6bf!important;background:#e7f7ef!important;color:#16724b!important;box-shadow:none!important}
@media(max-width:767.98px){.gp-adm-filters{grid-template-columns:1fr!important}.gp-adm-toolbar,.gp-adm-toolbar-actions{align-items:stretch;flex-direction:column}.gp-adm-total{justify-content:space-between}.gp-adm-toolbar .btn{width:100%}}
</style>
